import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  SpinnerGapIcon, ArrowSquareOutIcon, CopyIcon, SealCheckIcon, UsersThreeIcon, MagnifyingGlassIcon,
  WarningIcon, StarIcon, LightningIcon, SparkleIcon, FireIcon, PulseIcon, BellRingingIcon, BellSlashIcon,
  XIcon, FloppyDiskIcon, TrendUpIcon,
} from "@phosphor-icons/react";
import {
  fetchRobinhoodLaunches, fetchRobinhoodWallets, addRobinhoodWallet, deleteRobinhoodWallet,
  fetchRobinhoodFresh, fetchRobinhoodSurge, fetchRobinhoodPulse,
  fetchRhWatchlists, saveRhWatchlist, toggleRhWatchlist, deleteRhWatchlist,
} from "../lib/api";
import { getClientId } from "../lib/clientId";
import { markRobinhoodSeen } from "../lib/useRobinhoodAlerts";
import { useSeo } from "../lib/useSeo";

const RH = "#00C805"; // Robinhood green
const SCORE_COLORS = { strong: "#00E599", decent: "#7FD4FF", weak: "#F5A623", trash: "#FF3366" };
const scoreColor = (v) => SCORE_COLORS[v] || "#8A8F98";

const short = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");
const ago = (ts) => {
  if (!ts) return "";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const fmtNum = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${Math.round(v)}`;
};

const TokenRow = ({ t, matchKw, timeLabel }) => {
  const copy = () => navigator.clipboard.writeText(t.address).then(() => toast.success("Contract address copied")).catch(() => {});
  return (
    <div
      data-testid={`rh-token-${t.address}`}
      className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: t.likelyReal ? RH : "rgba(255,255,255,0.08)", background: t.likelyReal ? `${RH}0f` : "transparent" }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {t.likelyReal && (
            <span data-testid="rh-likely-real" className="font-mono text-[10px] px-1.5 py-0.5 font-bold inline-flex items-center gap-1" style={{ background: `${RH}22`, border: `1px solid ${RH}`, color: RH }}>
              <StarIcon size={11} weight="fill" /> MOST LIKELY REAL
            </span>
          )}
          <span className="font-heading font-bold">${(t.symbol || "?").toUpperCase()}</span>
          {t.score != null && (
            <span data-testid={`rh-score-${t.address}`} title={`Quality score ${t.score}/100 — ${t.verdict}. Blends holders, verified/certified contract, market cap & volume.`} className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: `${scoreColor(t.verdict)}18`, border: `1px solid ${scoreColor(t.verdict)}`, color: scoreColor(t.verdict) }}>
              {t.score}/100 {t.verdict}
            </span>
          )}
          {t.verified && <SealCheckIcon size={15} weight="fill" style={{ color: RH }} title="Verified contract" />}
          {t.keyword && <span className="font-mono text-[10px] px-1.5 py-0.5 border border-white/12 text-muted-foreground">"{t.keyword}"</span>}
          {matchKw && (
            <span data-testid={`rh-match-${t.address}`} title={`Matches your saved preset keyword "${matchKw}"`} className="font-mono text-[10px] px-1.5 py-0.5 font-bold inline-flex items-center gap-1" style={{ background: "#7FD4FF22", border: "1px solid #7FD4FF", color: "#7FD4FF" }}>
              <BellRingingIcon size={10} weight="fill" /> your "{matchKw}"
            </span>
          )}
          {t.holderSurge && (
            <span data-testid={`rh-holder-surge-${t.address}`} title={`+${t.holderSurge.delta} holders in ~${t.holderSurge.sinceMin}m (${t.holderSurge.pct}% ↑)`} className="font-mono text-[10px] px-1.5 py-0.5 font-bold inline-flex items-center gap-1 animate-pulse" style={{ background: "#ff6a0022", border: "1px solid #ff8c00", color: "#ff8c00" }}>
              🔥 holders climbing fast
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate mt-0.5">{t.name || "—"}</div>
        <div className="flex items-center gap-3 mt-1 font-mono text-[11px] flex-wrap">
          <span className="inline-flex items-center gap-1" style={{ color: (t.holders || 0) > 0 ? RH : "#888" }}>
            <UsersThreeIcon size={12} weight="fill" /> {t.holders != null ? `${Number(t.holders).toLocaleString()} holders` : "holders ?"}
            {t.holderSurge && <span style={{ color: "#ff8c00" }} className="font-bold">+{t.holderSurge.delta}</span>}
          </span>
          {t.marketCap != null && <span className="text-muted-foreground">mcap ${fmtNum(t.marketCap)}</span>}
          <button onClick={copy} data-testid={`rh-copy-${t.address}`} className="inline-flex items-center gap-1 text-muted-foreground hover:text-white transition-colors">
            <CopyIcon size={12} /> {short(t.address)}
          </button>
          {timeLabel && <span className="text-muted-foreground/60">{timeLabel}</span>}
        </div>
      </div>
      <a href={t.explorer} target="_blank" rel="noopener noreferrer" data-testid={`rh-explorer-${t.address}`} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors" style={{ border: `1px solid ${RH}66`, color: RH }}>
        Explorer <ArrowSquareOutIcon size={13} />
      </a>
      <a href={`https://axiom.trade/t/${t.address}`} target="_blank" rel="noopener noreferrer" data-testid={`rh-axiom-${t.address}`} title="Buy on Axiom" className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-black bg-neon hover:bg-neon/90 transition-colors">
        <LightningIcon size={13} weight="fill" /> Buy
      </a>
    </div>
  );
};

const Spark = ({ data }) => {
  if (!data || data.length < 2) return null;
  const w = 84, h = 30;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - 3 - ((v - min) / range) * (h - 6)}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" data-testid="rh-spark" aria-label="holder growth curve">
      <title>Holder growth curve ({min.toLocaleString()} → {max.toLocaleString()})</title>
      <polyline points={pts} fill="none" stroke="#ff8c00" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={w} cy={h - 3 - ((data[data.length - 1] - min) / range) * (h - 6)} r="2" fill="#ff8c00" />
    </svg>
  );
};

const PulseTile = ({ label, value, sub, accent }) => (
  <div className="flex-1 min-w-[130px] border border-white/10 bg-black/25 px-3.5 py-2.5">
    <div className="font-mono font-black text-lg leading-tight" style={accent ? { color: accent } : {}}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
    {sub && <div className="font-mono text-[10px] text-muted-foreground/70 mt-0.5 truncate">{sub}</div>}
  </div>
);

const ChainPulse = () => {
  const { data } = useQuery({ queryKey: ["rhPulse"], queryFn: fetchRobinhoodPulse, refetchInterval: 60000 });
  if (!data) return null;
  const c = data.chain || {};
  const leader = data.surgeLeader;
  return (
    <div className="flex gap-2 flex-wrap" data-testid="rh-pulse">
      <PulseTile label="New tokens · 24h" value={data.newToday ?? "—"} accent={RH} />
      <PulseTile label="Active this hour" value={data.activeHour ?? "—"} />
      <PulseTile
        label="Hottest right now"
        value={leader ? `$${(leader.symbol || "?").toUpperCase()}` : "—"}
        sub={leader ? `+${leader.delta} holders / ${leader.windowMin}m` : "watching…"}
        accent={leader ? "#ff8c00" : undefined}
      />
      <PulseTile label="Tokens indexed" value={fmtNum(data.indexed)} />
      <PulseTile label="ETH" value={c.ethPrice ? `$${Math.round(c.ethPrice).toLocaleString()}` : "—"} sub={c.avgBlockTime ? `~${(c.avgBlockTime / 1000).toFixed(1)}s blocks` : null} />
    </div>
  );
};

const WalletWatch = () => {
  const cid = getClientId();
  const [addr, setAddr] = useState("");
  const [label, setLabel] = useState("");
  const { data, isLoading, refetch } = useQuery({ queryKey: ["rhWallets", cid], queryFn: () => fetchRobinhoodWallets(cid), refetchInterval: 90000 });
  const wallets = data?.wallets || [];
  const add = async () => {
    const a = addr.trim();
    if (!(a.startsWith("0x") && a.length === 42)) { toast.error("Enter a valid 0x address (42 chars)"); return; }
    try { await addRobinhoodWallet(cid, a, label.trim()); setAddr(""); setLabel(""); toast.success("Wallet added — watching for new moves"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add wallet"); }
  };
  const remove = async (a) => { try { await deleteRobinhoodWallet(cid, a); toast.success("Removed"); refetch(); } catch { toast.error("Remove failed"); } };
  return (
    <div className="border p-4 sm:p-5 space-y-4" style={{ borderColor: `${RH}33`, background: `${RH}08` }} data-testid="rh-wallet-watch">
      <div>
        <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2">👁 Tracked Wallets <span className="font-mono text-xs text-muted-foreground">{wallets.length}</span></h2>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">Watch any <span className="text-white">Robinhood Chain <code className="text-[11px]">0x</code> wallet</span> (insiders, teams, whales). When it acquires or deploys a brand-new token, you get a loud Telegram ping (enable "Robinhood wallet moves" on the Alerts page). Only you see the wallets you add.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input data-testid="rh-wallet-addr" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x… wallet address" className="flex-1 bg-black/40 border border-white/12 focus:border-neon/50 outline-none px-3 py-2 text-sm font-mono placeholder:font-sans placeholder:text-muted-foreground/50" />
        <input data-testid="rh-wallet-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label (e.g. whale #1)" className="sm:w-52 bg-black/40 border border-white/12 focus:border-neon/50 outline-none px-3 py-2 text-sm placeholder:text-muted-foreground/50" />
        <button data-testid="rh-wallet-add" onClick={add} className="px-4 py-2 font-semibold text-black bg-neon hover:bg-neon/90 transition-colors">Track</button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6"><SpinnerGapIcon size={24} className="animate-spin text-neon" /></div>
      ) : wallets.length === 0 ? (
        <div className="text-[11px] font-mono text-muted-foreground/60" data-testid="rh-wallet-empty">No wallets tracked yet — paste a 0x address above to start.</div>
      ) : (
        <div className="space-y-3">
          {wallets.map((w) => (
            <div key={w.address} className="border border-white/10 bg-black/20 p-3" data-testid={`rh-wallet-${w.address}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{w.label}</div>
                  <a href={w.walletExplorer} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-muted-foreground hover:text-white">{short(w.address)} ↗</a>
                </div>
                <button onClick={() => remove(w.address)} data-testid={`rh-wallet-remove-${w.address}`} className="text-muted-foreground/60 hover:text-red-400 transition-colors p-1 text-xs shrink-0">remove</button>
              </div>
              {w.recent?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {[...w.recent].sort((a, b) => (b.ts || 0) - (a.ts || 0)).map((t) => (
                    <a key={t.address} href={t.axiom} target="_blank" rel="noopener noreferrer" data-testid={`rh-wallet-token-${t.address}`} title={`${t.name || ""}${t.isDeploy ? " — DEPLOYED by this wallet" : ""} — buy on Axiom`} className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono border transition-colors ${t.isDeploy ? "border-neon text-neon font-bold" : "border-white/12 hover:border-neon/50 hover:text-neon"}`}>
                      {t.isDeploy && "🚀"}${(t.symbol || "?").toUpperCase()} <LightningIcon size={11} weight="fill" style={{ color: RH }} />
                      {t.ts ? <span className="text-muted-foreground/60 font-normal">· {ago(t.ts)}</span> : null}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] font-mono text-muted-foreground/60">no recent token activity found</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Presets = ({ cid, input, setInput, apply }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["rhWatchlists", cid], queryFn: () => fetchRhWatchlists(cid) });
  const lists = data?.watchlists || [];
  const [name, setName] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["rhWatchlists", cid] });

  const save = async () => {
    const kws = input.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
    if (!name.trim()) { toast.error("Give the preset a name"); return; }
    if (!kws.length) { toast.error("Type some keywords first"); return; }
    try { await saveRhWatchlist(cid, { name: name.trim(), keywords: kws, alert: false }); setName(""); toast.success(`Preset "${name.trim()}" saved`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save preset"); }
  };
  const toggle = async (w) => {
    try { const r = await toggleRhWatchlist(cid, w.wid); toast.success(r.alert ? `🔔 Telegram alerts ON for "${w.name}"` : `Alerts off for "${w.name}"`); refresh(); }
    catch { toast.error("Toggle failed"); }
  };
  const del = async (w) => { try { await deleteRhWatchlist(cid, w.wid); toast.success("Preset deleted"); refresh(); } catch { toast.error("Delete failed"); } };

  return (
    <div className="space-y-2" data-testid="rh-presets">
      {lists.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Presets:</span>
          {lists.map((w) => (
            <span key={w.wid} data-testid={`rh-preset-${w.wid}`} className="inline-flex items-center border border-white/12 bg-black/25 text-[11px] font-mono">
              <button data-testid={`rh-preset-apply-${w.wid}`} onClick={() => { setInput(w.keywords.join(", ")); apply(w.keywords.join(",")); }} title={`Search: ${w.keywords.join(", ")}`} className="px-2 py-1 hover:text-neon transition-colors">
                {w.name}
              </button>
              <button data-testid={`rh-preset-alert-${w.wid}`} onClick={() => toggle(w)} title={w.alert ? "Telegram alerts ON for this preset — click to turn off" : "Turn ON Telegram alerts for this preset's keywords"} className={`px-1.5 py-1 border-l border-white/10 transition-colors ${w.alert ? "text-neon" : "text-muted-foreground/50 hover:text-white"}`}>
                {w.alert ? <BellRingingIcon size={12} weight="fill" /> : <BellSlashIcon size={12} />}
              </button>
              <button data-testid={`rh-preset-del-${w.wid}`} onClick={() => del(w)} title="Delete preset" className="px-1.5 py-1 border-l border-white/10 text-muted-foreground/50 hover:text-red-400 transition-colors">
                <XIcon size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input data-testid="rh-preset-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Save current keywords as preset… (name)" className="flex-1 sm:max-w-[280px] bg-black/30 border border-white/10 focus:border-neon/40 outline-none px-2.5 py-1.5 text-xs placeholder:text-muted-foreground/50" />
        <button data-testid="rh-preset-save" onClick={save} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-xs font-semibold text-muted-foreground hover:text-white hover:border-white/30 transition-colors">
          <FloppyDiskIcon size={13} /> Save preset
        </button>
      </div>
    </div>
  );
};

const LS_Q = "alphaapeing_rh_last_q";

export default function RobinhoodFeed() {
  useSeo({
    title: "Robinhood Chain Radar — Fresh Mints, Holder Surge & Keyword Watch | AlphaApeing",
    description: "Live Robinhood Chain (Ethereum L2) radar: newest token mints, tokens gaining holders the fastest, keyword launch alerts and wallet tracking.",
  });
  const cid = getClientId();
  const [view, setView] = useState("fresh");
  const [input, setInput] = useState(() => localStorage.getItem(LS_Q)?.split(",").join(", ") || "");
  const [q, setQ] = useState(() => localStorage.getItem(LS_Q) || "");

  const freshQ = useQuery({ queryKey: ["rhFresh"], queryFn: fetchRobinhoodFresh, refetchInterval: 60000 });
  const surgeQ = useQuery({ queryKey: ["rhSurge"], queryFn: () => fetchRobinhoodSurge(90), refetchInterval: 60000, enabled: view === "surge" });
  const kwQ = useQuery({ queryKey: ["robinhoodLaunches", q], queryFn: () => fetchRobinhoodLaunches(q), refetchInterval: 60000, enabled: !!q });
  const { data: wlData } = useQuery({ queryKey: ["rhWatchlists", cid], queryFn: () => fetchRhWatchlists(cid) });

  const presetKws = useMemo(() => {
    const out = [];
    for (const w of wlData?.watchlists || []) for (const k of w.keywords || []) out.push(k);
    return [...new Set(out)];
  }, [wlData]);
  const matchKw = (t) => {
    const hay = `${t.name || ""} ${t.symbol || ""}`.toLowerCase();
    return presetKws.find((k) => hay.includes(k)) || null;
  };

  const freshTokens = freshQ.data?.tokens || [];
  const surgeTokens = surgeQ.data?.tokens || [];
  const kwTokens = kwQ.data?.tokens || [];
  const [hideTrash, setHideTrash] = useState(true);
  const trashCount = kwTokens.filter((t) => (t.score || 0) < 20).length;
  const visibleKw = hideTrash ? kwTokens.filter((t) => (t.score || 0) >= 20) : kwTokens;
  const { data: walletData } = useQuery({ queryKey: ["rhWallets", cid], queryFn: () => fetchRobinhoodWallets(cid), refetchInterval: 90000 });

  useEffect(() => {
    const addrs = [...freshTokens.map((t) => t.address), ...kwTokens.map((t) => t.address)];
    for (const w of walletData?.wallets || []) for (const t of w.recent || []) addrs.push(t.address);
    if (addrs.length) markRobinhoodSeen(addrs.filter(Boolean));
  }, [freshQ.data, kwQ.data, walletData]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (raw) => {
    const cleaned = (raw ?? input).split(/[\n,]+/).map((x) => x.trim()).filter(Boolean).join(",");
    setQ(cleaned);
    localStorage.setItem(LS_Q, cleaned);
    setView("keywords");
  };

  const tabs = [
    { id: "fresh", label: "Fresh Mints", Icon: SparkleIcon, count: freshTokens.length },
    { id: "surge", label: "Holder Surge", Icon: FireIcon, count: surgeTokens.length || null },
    { id: "keywords", label: "Keyword Watch", Icon: MagnifyingGlassIcon, count: q ? visibleKw.length : null },
  ];

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="robinhood-feed-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black text-2xl font-black" style={{ background: RH }}><PulseIcon size={26} weight="bold" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            ROBINHOOD <span style={{ color: RH }}>RADAR</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Mission control for <span className="text-white font-semibold">Robinhood Chain</span> (an Ethereum L2):
          the <span style={{ color: RH }}>newest mints</span> as they start moving, a live board of tokens{" "}
          <span style={{ color: "#ff8c00" }}>gaining holders the fastest</span>, and your own{" "}
          <span className="text-white">keyword launch alerts</span> — holders is the best real-vs-copycat signal on this chain.
        </p>
      </div>

      <ChainPulse />

      <div className="flex border border-white/10 w-fit max-w-full overflow-x-auto">
        {tabs.map(({ id, label, Icon, count }) => (
          <button key={id} data-testid={`rh-tab-${id}`} onClick={() => setView(id)}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${view === id ? "text-black" : "text-muted-foreground hover:text-white"}`}
            style={view === id ? { background: RH } : {}}>
            <Icon size={15} weight={view === id ? "fill" : "regular"} /> {label}
            {count != null && count > 0 && <span className="font-mono text-[10px] opacity-70">({count})</span>}
          </button>
        ))}
      </div>

      {view === "fresh" && (
        <div className="space-y-2" data-testid="rh-fresh-section">
          <p className="text-xs text-muted-foreground font-mono">Tokens first spotted moving on-chain, newest first — refreshed every minute. New here = high risk, DYOR.</p>
          {freshQ.isLoading ? (
            <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: RH }} /></div>
          ) : freshTokens.length === 0 ? (
            <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-xs px-6" data-testid="rh-fresh-empty">
              Indexing the chain now — brand-new tokens will appear here within minutes of their first transfer.
            </div>
          ) : (
            <div className="border border-white/5 divide-y divide-white/5">
              {freshTokens.map((t) => <TokenRow key={t.address} t={t} matchKw={matchKw(t)} timeLabel={`spotted ${ago(t.firstSeen)}`} />)}
            </div>
          )}
        </div>
      )}

      {view === "surge" && (
        <div className="space-y-2" data-testid="rh-surge-section">
          <p className="text-xs text-muted-foreground font-mono">Tokens gaining holders the fastest over the last ~90 minutes — what's actually being aped right now.</p>
          {surgeQ.isLoading ? (
            <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: "#ff8c00" }} /></div>
          ) : surgeTokens.length === 0 ? (
            <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-xs px-6" data-testid="rh-surge-empty">
              Building holder history — the surge board fills in as we snapshot holder counts (give it ~10 minutes after a fresh deploy).
            </div>
          ) : (
            <div className="border border-white/5 divide-y divide-white/5">
              {surgeTokens.map((t, i) => (
                <div key={t.address} className="flex items-center gap-3">
                  <span className="pl-4 font-mono font-black text-lg w-8 shrink-0" style={{ color: i < 3 ? "#ff8c00" : "#555" }}>#{i + 1}</span>
                  <Spark data={t.spark} />
                  <div className="flex-1 min-w-0">
                    <TokenRow t={t} matchKw={matchKw(t)} timeLabel={
                      <span style={{ color: "#ff8c00" }} className="font-bold">
                        <TrendUpIcon size={11} className="inline mr-0.5" />+{t.delta} holders / {t.windowMin}m (~{t.perHour}/hr)
                      </span>
                    } />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "keywords" && (
        <div className="space-y-3" data-testid="rh-keywords-section">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 bg-black/40 border border-white/12 focus-within:border-neon/50 px-3">
              <MagnifyingGlassIcon size={15} className="text-muted-foreground shrink-0" />
              <input
                data-testid="rh-keywords-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                placeholder="Your keywords, comma-separated — e.g. doge, ai agent, robinhood"
                className="w-full bg-transparent outline-none py-2.5 text-sm placeholder:text-muted-foreground/50"
              />
            </div>
            <button data-testid="rh-search-btn" onClick={() => apply()} className="px-5 py-2 font-semibold text-black transition-colors" style={{ background: RH }}>
              Search chain
            </button>
          </div>

          <Presets cid={cid} input={input} setInput={setInput} apply={apply} />

          <div className="flex items-start gap-2 text-[11px] font-mono p-3 border" style={{ borderColor: "#F0C00033", background: "#F0C00008", color: "#e8c86a" }}>
            <WarningIcon size={14} weight="fill" className="shrink-0 mt-0.5" />
            Copycats flood popular keywords. Holders is the best real-vs-fake signal, but always double-check the contract on the explorer before buying. Not financial advice.
          </div>

          {!q ? (
            <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-xs px-6" data-testid="rh-kw-empty">
              Type keywords above (or tap a preset) to watch launches matching them. Enable the Telegram ping per preset with the 🔔, or globally on the Alerts page.
            </div>
          ) : kwQ.isLoading ? (
            <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: RH }} /></div>
          ) : kwTokens.length === 0 ? (
            <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-xs px-6" data-testid="rh-empty">
              No tokens on Robinhood Chain match those keywords yet. When one launches it'll appear here (and ping your Telegram if enabled).
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{visibleKw.length} shown · ranked by quality score</span>
                <div className="flex items-center gap-3">
                  {trashCount > 0 && (
                    <button data-testid="rh-hidetrash-toggle" onClick={() => setHideTrash((v) => !v)} className="font-mono text-xs transition-colors" style={{ color: hideTrash ? "#FF3366" : "#888" }}>
                      {hideTrash ? `🗑️ ${trashCount} trash hidden — show` : "hide trash"}
                    </button>
                  )}
                  <button onClick={() => kwQ.refetch()} className="font-mono text-xs text-muted-foreground hover:text-white transition-colors">{kwQ.isFetching ? "refreshing…" : "refresh"}</button>
                </div>
              </div>
              <div className="border border-white/5 divide-y divide-white/5">
                {visibleKw.map((t) => <TokenRow key={t.address} t={t} timeLabel={t.firstSeen ? `seen ${ago(t.firstSeen)}` : null} />)}
              </div>
            </div>
          )}
        </div>
      )}

      <WalletWatch />
    </div>
  );
}
